"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFormSetter = exports.useFormGetter = exports.useInputValues = exports.useInternalFormInputController = exports.getInternalFormStore = void 0;
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const FormScope_1 = require("./FormScope");
const lib_1 = require("./lib");
function detachedStore() {
    return (0, app_1.createStore)(lib_1.formReducer, {
        inputs: (0, app_1.createStore)(lib_1.inputsReducer, {}),
        pages: [],
        aggregators: {},
        submitting: false,
        mountedRef: { current: true },
        __detached: true,
    });
}
exports.getInternalFormStore = (() => {
    const formStoreMap = {};
    return (id) => {
        var _a;
        formStoreMap[id] = (_a = formStoreMap[id]) !== null && _a !== void 0 ? _a : detachedStore();
        return formStoreMap[id];
    };
})();
function useInternalFormInputController(name, formId) {
    const id = usePersistedFormId(formId);
    return (0, exports.getInternalFormStore)(id)('inputs')[0].inputs(name)[0][name];
}
exports.useInternalFormInputController = useInternalFormInputController;
function useInputValues(...input) {
    const args = [...input];
    return useInputValuesBase(typeof args[0] === 'object' ? args.shift() : {}, ...args);
}
exports.useInputValues = useInputValues;
function useInputValuesBase(options, ...names) {
    const id = usePersistedFormId();
    const formScope = (0, FormScope_1.useFormScope)();
    const ignoreScope = (options === null || options === void 0 ? void 0 : options.ignoreScope) || names.some((name) => name.includes('.'));
    const scope = ignoreScope ? undefined : formScope;
    const scoped = scope ? names.map((name) => [scope, name].join('.')) : names;
    const [inputsState] = (0, exports.getInternalFormStore)(id)('inputs')[0].inputs(...scoped);
    return Object.fromEntries(Object.entries(inputsState).map(([k, input]) => [
        scope ? (0, utils_1.last)(k.split('.')) : k,
        input === null || input === void 0 ? void 0 : input.value,
    ]));
}
function usePersistedFormId(id) {
    var _a;
    const idRef = (0, react_1.useRef)(id);
    const parentFormId = (0, react_1.useContext)(lib_1.InternalFormIdContext);
    const formId = (_a = idRef.current) !== null && _a !== void 0 ? _a : parentFormId;
    if (!formId) {
        throw new Error('Either pass formId or use inside <Form>');
    }
    return formId;
}
function useFormGetter(id) {
    const formId = usePersistedFormId(id);
    const formScope = (0, FormScope_1.useFormScope)();
    return (0, react_1.useCallback)((baseName, options) => {
        var _a;
        const ignoreScope = (options === null || options === void 0 ? void 0 : options.ignoreScope) || baseName.includes('.');
        const scope = ignoreScope ? undefined : formScope;
        const name = [scope, baseName].filter(Boolean).join('.');
        return (_a = (0, exports.getInternalFormStore)(formId).getState().inputs.getState()[name]) === null || _a === void 0 ? void 0 : _a.value;
    }, [formId, formScope]);
}
exports.useFormGetter = useFormGetter;
function useFormSetter(id) {
    const formId = usePersistedFormId(id);
    const useStore = (0, exports.getInternalFormStore)(formId);
    const formScope = (0, FormScope_1.useFormScope)();
    const [, dispatch] = useStore();
    return (0, react_1.useCallback)((baseName, value, options) => {
        var _a;
        const ignoreScope = (options === null || options === void 0 ? void 0 : options.ignoreScope) || baseName.includes('.');
        const scope = ignoreScope ? undefined : formScope;
        const name = [scope, baseName].filter(Boolean).join('.');
        const controller = useStore.getState().inputs.getState()[name];
        const update = typeof value === 'function' ? value : () => value;
        const isNew = !controller || controller.__defaultController;
        const currentValue = isNew ? undefined : controller.value;
        const newValue = update(currentValue);
        if ((0, utils_1.isNullish)(newValue)) {
            return;
        }
        if (isNew) {
            if (controller) {
                if (options === null || options === void 0 ? void 0 : options.forceUpdateWithDispatch) {
                    dispatch('UPDATE_INPUT', { name, value: newValue });
                }
                else {
                    controller.setValue(newValue);
                }
            }
            else {
                dispatch('REGISTER_INPUT', {
                    name,
                    value: newValue,
                    aggregator: (_a = options === null || options === void 0 ? void 0 : options.aggregator) !== null && _a !== void 0 ? _a : null,
                    pageId: null,
                    __defaultController: true,
                });
            }
        }
        else {
            const type = typeof currentValue;
            const newType = typeof newValue;
            if (newType !== type) {
                throw new TypeError(`${newValue} (${newType}) is not assignable to input "${name}" of type ${type}`);
            }
            controller.setValue(newValue);
        }
    }, [dispatch, useStore, formScope]);
}
exports.useFormSetter = useFormSetter;
